import { Award, CalendarCheck, FileSearch } from "lucide-react";
import type { ReactNode } from "react";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { ErrorState } from "../molecules/ErrorState";
import {
	HealthSummaryCard,
	type HealthSummaryCardProps,
} from "../molecules/HealthSummaryCard";
import { SearchField } from "../molecules/SearchField";
import { Skeleton } from "../molecules/Skeleton";
import {
	RecentActivityFeed,
	type RecentActivityFeedProps,
} from "../organisms/RecentActivityFeed";
import {
	TamperAlertFeed,
	type TamperAlertItem,
} from "../organisms/TamperAlertFeed";
import { TaskLauncher, type TaskLauncherItem } from "../organisms/TaskLauncher";

const SEARCH_INPUT_ID = "dashboard-verify-search";

export interface DashboardPageProps {
	health: HealthSummaryCardProps;
	tamperAlerts: TamperAlertItem[];
	onSelectAlert?: (alert: TamperAlertItem) => void;
	recentActivity: RecentActivityFeedProps["items"];
	loading?: boolean;
	error?: Error | null;
	className?: string;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	onSearchSubmit?: () => void;
	onVerifyLoan?: () => void;
	onVerifyEvent?: () => void;
	onGetCertificate?: () => void;
}

function DashboardPage({
	health,
	tamperAlerts,
	onSelectAlert,
	recentActivity,
	loading = false,
	error = null,
	className,
	searchValue,
	onSearchChange,
	onSearchSubmit,
	onVerifyLoan,
	onVerifyEvent,
	onGetCertificate,
}: DashboardPageProps): ReactNode {
	if (error) {
		return (
			<ErrorState
				error={error}
				title="Failed to load dashboard"
				className={cn("py-24", className)}
			/>
		);
	}

	const showSearch =
		onSearchChange !== undefined && onSearchSubmit !== undefined;

	const taskItems: TaskLauncherItem[] = [
		{
			id: "verify-loan",
			title: "Verify a loan",
			description: "Confirm a loan's records match the official ledger.",
			icon: <FileSearch className="h-5 w-5" aria-hidden="true" />,
			onClick: onVerifyLoan,
		},
		{
			id: "verify-event",
			title: "Verify an event",
			description: "Check that a recorded event is locked in and unaltered.",
			icon: <CalendarCheck className="h-5 w-5" aria-hidden="true" />,
			onClick: onVerifyEvent,
		},
		{
			id: "get-certificate",
			title: "Get a certificate",
			description: "Download a signed verification certificate.",
			icon: <Award className="h-5 w-5" aria-hidden="true" />,
			onClick: onGetCertificate,
		},
	];

	return (
		<div className={cn("flex flex-col gap-8", className)}>
			{/* Verification hero — the primary task auditors come here to do */}
			<header className="flex flex-col gap-5">
				<div className="flex flex-col gap-2">
					<Heading as="h1" size="2xl">
						Verify a loan or event record
					</Heading>
					<Text textColor="mutedForeground" className="max-w-prose">
						Look up any loan or event to confirm its records have not been
						altered.
					</Text>
				</div>

				{showSearch && (
					<div className="flex flex-col gap-1.5">
						<label
							htmlFor={SEARCH_INPUT_ID}
							className="text-sm font-medium text-foreground"
						>
							Find a record to verify
						</label>
						<SearchField
							id={SEARCH_INPUT_ID}
							value={searchValue ?? ""}
							onChange={onSearchChange}
							placeholder="Enter a loan ID or event reference"
							onClear={() => onSearchChange("")}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									onSearchSubmit();
								}
							}}
						/>
						<Text size="xs" textColor="mutedForeground">
							For example, a loan ID like LN-48213 or an event reference
							starting with 0x.
						</Text>
					</div>
				)}

				<TaskLauncher items={taskItems} headingAs="h2" />
			</header>

			{/* System verdict — kept below the hero, a single non-contradictory status */}
			<section className="flex flex-col gap-4">
				<Heading size="md">System status</Heading>
				{loading ? (
					<div className="flex flex-col gap-4">
						<Skeleton className="h-32 w-full" />
						<Skeleton className="h-16 w-full" />
					</div>
				) : (
					<>
						<HealthSummaryCard {...health} />
						<TamperAlertFeed
							alerts={tamperAlerts}
							onSelectAlert={onSelectAlert}
						/>
					</>
				)}
			</section>

			{/* Recent activity — secondary context */}
			<section className="flex flex-col gap-3">
				{loading ? (
					<div className="flex flex-col gap-3">
						<Skeleton className="h-6 w-36" variant="text" />
						{Array.from({ length: 5 }).map((_, i) => {
							// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
							return <Skeleton key={i} className="h-16" />;
						})}
					</div>
				) : (
					<RecentActivityFeed items={recentActivity} />
				)}
			</section>
		</div>
	);
}
DashboardPage.displayName = "DashboardPage";

export default DashboardPage;
export { DashboardPage };
